import { useRef, useState } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { X, PenTool } from "lucide-react";
import { Reveal } from "./Reveal";
import { CREATIVE } from "../../data/content";

const ParallaxCard = ({ item, onOpen }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <Reveal className={`${item.span} h-full`}>
      <button
        ref={ref}
        onClick={() => onOpen(item)}
        data-testid={`creative-${item.key}`}
        className="group relative flex h-full w-full overflow-hidden rounded-2xl border border-line bg-sand min-h-[240px]"
      >
        <div className="overflow-hidden absolute inset-0">
          <motion.img
            style={{ y }}
            src={item.src}
            alt={item.title}
            loading="lazy"
            className="w-full h-full object-cover scale-110 transition-transform duration-700 group-hover:scale-[1.18]"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/0 to-ink/0 opacity-70 group-hover:opacity-90 transition-opacity duration-500" />
        <div className="absolute left-5 bottom-5 right-5 text-left translate-y-1 group-hover:translate-y-0 transition-transform duration-500">
          <p className="text-[0.65rem] uppercase tracking-[0.18em] text-paper/70">{item.tag}</p>
          <p className="font-serif text-2xl md:text-3xl text-paper tracking-tight leading-tight mt-1">
            {item.title}
          </p>
        </div>
      </button>
    </Reveal>
  );
};

export const GraphicDesign = () => {
  const [active, setActive] = useState(null);

  return (
    <section id="graphic" data-testid="graphic-section" className="bg-sand border-y border-line">
      <div className="mx-auto max-w-[1500px] px-6 md:px-12 py-24 md:py-40">
        <Reveal className="max-w-4xl">
          <p className="overline text-terracotta flex items-center gap-2">
            <PenTool size={14} /> Graphic Design
          </p>
          <h2 className="mt-6 font-serif text-5xl md:text-7xl tracking-tight leading-[0.95] text-ink">
            Twenty years
            <br />
            of design work.
          </h2>
          <p className="mt-8 text-base md:text-lg text-stone leading-relaxed font-light border-l-2 border-terracotta pl-5 md:pl-6 max-w-2xl">
            Event flyers, brand identities, editorial layouts and product mockups, created for
            events, artists and creators, including years of work for Timbaland Way Cares around
            Virginia Beach, VA.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-12 auto-rows-[240px] md:auto-rows-[260px] gap-5 md:gap-6 grid-flow-row-dense">
          {CREATIVE.map((item) => (
            <ParallaxCard key={item.key} item={item} onOpen={setActive} />
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="graphic-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setActive(null)}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/90 backdrop-blur-md p-4 md:p-10"
          >
            <button
              data-testid="graphic-modal-close"
              onClick={() => setActive(null)}
              className="absolute right-5 top-5 grid place-items-center w-11 h-11 rounded-full bg-paper text-ink hover:bg-terracotta hover:text-paper transition-colors duration-300"
              aria-label="Close"
            >
              <X size={20} />
            </button>
            <motion.img
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              src={active.src}
              alt={active.title}
              className="max-h-[85vh] max-w-full w-auto rounded-xl object-contain"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
